import { defaultPolicies } from './omniChatConstants';
import type {
  OmniChatAgent,
  OmniChatConnector,
  OmniChatGateway,
  OmniChatKnowledgeBase,
  OmniChatMonitorInsight,
} from './omniChatTypes';

export const SYNTHETIC_GATEWAYS: OmniChatGateway[] = [
  { id: 'gw-care', name: 'Customer Care GPT', model: 'gpt-4o' },
  { id: 'gw-research', name: 'Research Claude', model: 'claude-sonnet-4' },
  { id: 'gw-internal', name: 'Internal Mix', model: 'llama-3.1-70b' },
];

export const SYNTHETIC_CONNECTORS: OmniChatConnector[] = [
  { id: 'sql_server', label: 'SQL Server', hint: 'Query operational tables', group: 'data' },
  { id: 'sharepoint', label: 'SharePoint', hint: 'Read workplace files', group: 'files' },
  { id: 'slack', label: 'Slack', hint: 'Post and search channels', group: 'chat' },
  { id: 'teams', label: 'Teams', hint: 'Meetings and chats', group: 'chat' },
  { id: 'knowledge', label: 'Knowledge', hint: 'Search indexed articles', group: 'files' },
  { id: 'web', label: 'Web', hint: 'Look up public pages', group: 'web' },
];

export const CONNECTOR_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'data', label: 'Data' },
  { id: 'chat', label: 'Chat' },
  { id: 'files', label: 'Files' },
  { id: 'web', label: 'Web' },
] as const;

export const SYNTHETIC_KNOWLEDGE_BASES: OmniChatKnowledgeBase[] = [
  { id: 'kb-hr-benefits', name: 'HR Benefits', workspace: 'People', workspaceId: 'ws-people' },
  { id: 'kb-hr-leave', name: 'Leave Policy', workspace: 'People', workspaceId: 'ws-people' },
  { id: 'kb-sales-playbook', name: 'Sales Playbook', workspace: 'Revenue', workspaceId: 'ws-revenue' },
  { id: 'kb-product-docs', name: 'Product Docs', workspace: 'Product', workspaceId: 'ws-product' },
  { id: 'kb-it-runbooks', name: 'IT Runbooks', workspace: 'Operations', workspaceId: 'ws-operations' },
  { id: 'kb-security', name: 'Security FAQ', workspace: 'Security', workspaceId: 'ws-security' },
];

export const KNOWLEDGE_BASE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'People', label: 'People' },
  { id: 'Revenue', label: 'Revenue' },
  { id: 'Product', label: 'Product' },
  { id: 'Operations', label: 'Operations' },
  { id: 'Security', label: 'Security' },
] as const;

export const STARTER_INSTRUCTIONS = [
  'You are a concise workplace assistant. Answer from the connected sources first.',
  'Ask one clarifying question when the request is ambiguous, then act.',
  'Keep replies short. Name the source when you use a connector.',
  'If you cannot find a reliable answer, say so and offer the next step.',
] as const;

export const SEED_AGENTS: OmniChatAgent[] = [
  {
    id: 'oc-benefits',
    title: 'Benefits Concierge',
    description: 'Answers enrollment, coverage, and leave questions from HR knowledge.',
    iconId: 'lifebuoy',
    instruction:
      'You help employees understand benefits. Use Knowledge and SharePoint first. Never invent policy details.',
    gatewayId: 'gw-care',
    inferenceQuotaId: '',
    inferenceKeyLast4: '',
    connectorIds: ['knowledge', 'sharepoint'],
    knowledgeBaseIds: ['kb-hr-benefits', 'kb-hr-leave'],
    policies: defaultPolicies(),
    active: true,
    createdBy: 'seed',
    createdByName: 'Selfic',
    createdByEmail: '',
    createdAt: '2026-10-18T14:20:00.000Z',
    updatedAt: '2026-10-18T14:20:00.000Z',
  },
  {
    id: 'oc-sales',
    title: 'Sales Briefing Agent',
    description: 'Drafts account briefs from CRM notes and recent Teams threads.',
    iconId: 'briefcase',
    instruction:
      'Prepare a one-page account brief. Lead with the ask, then risks, then next steps.',
    gatewayId: 'gw-research',
    inferenceQuotaId: '',
    inferenceKeyLast4: '',
    connectorIds: ['sql_server', 'teams', 'sharepoint'],
    knowledgeBaseIds: ['kb-sales-playbook'],
    policies: defaultPolicies(),
    active: true,
    createdBy: 'seed',
    createdByName: 'Selfic',
    createdByEmail: '',
    createdAt: '2026-10-24T09:05:00.000Z',
    updatedAt: '2026-10-24T09:05:00.000Z',
  },
  {
    id: 'oc-helpdesk',
    title: 'IT Help Desk',
    description: 'Triages common IT issues and points people to the right runbook.',
    iconId: 'headset',
    instruction:
      'Diagnose common IT issues in three steps. Offer a runbook link when one exists. Escalate when access or hardware is involved.',
    gatewayId: 'gw-internal',
    inferenceQuotaId: '',
    inferenceKeyLast4: '',
    connectorIds: ['knowledge', 'slack', 'web'],
    knowledgeBaseIds: ['kb-it-runbooks', 'kb-security'],
    policies: defaultPolicies(),
    active: true,
    createdBy: 'seed',
    createdByName: 'Selfic',
    createdByEmail: '',
    createdAt: '2026-10-29T16:40:00.000Z',
    updatedAt: '2026-10-29T16:40:00.000Z',
  },
];

export const MONITOR_PROVIDERS = [
  { id: 'all', label: 'All providers' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'meta', label: 'Meta' },
] as const;

export function gatewayProvider(gatewayId: string): string {
  const model = findGateway(gatewayId)?.model ?? '';
  if (model.includes('claude')) return 'anthropic';
  if (model.includes('llama')) return 'meta';
  return 'openai';
}

export function findGateway(id: string): OmniChatGateway | undefined {
  return SYNTHETIC_GATEWAYS.find((gateway) => gateway.id === id);
}

export function findConnector(id: string): OmniChatConnector | undefined {
  return SYNTHETIC_CONNECTORS.find((connector) => connector.id === id);
}

export function findKnowledgeBase(id: string): OmniChatKnowledgeBase | undefined {
  return SYNTHETIC_KNOWLEDGE_BASES.find((item) => item.id === id);
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function monitorDays(conversations: number[], latency: number[]): OmniChatMonitorInsight['days'] {
  return DAY_LABELS.map((label, index) => ({
    label,
    conversations: conversations[index] ?? 0,
    latencyMs: latency[index] ?? 0,
  }));
}

function connectorRow(
  id: string,
  ok: number,
  slow: number,
  error: number
): OmniChatMonitorInsight['connectors'][number] {
  return { id, label: findConnector(id)?.label ?? id, ok, slow, error };
}

const DEFAULT_MONITOR: OmniChatMonitorInsight = {
  conversations: 128,
  tokens: 84210,
  latencyMs: 640,
  errorRate: 0.8,
  health: { score: 86, label: 'Healthy' },
  delta: { conversations: 8.4, tokens: 3.1, latencyMs: -4.2, errorRate: -0.2 },
  days: monitorDays([18, 22, 19, 28, 31, 24, 26], [680, 650, 710, 620, 590, 640, 610]),
  policies: { allowed: 118, flagged: 7, blocked: 3 },
  connectors: [],
  groundedPct: 91,
  blocksToday: 2,
  activity: [
    { id: 'a1', when: 'Today 09:14', event: 'Conversation completed from knowledge', status: 'ok' },
    { id: 'a2', when: 'Today 08:41', event: 'Connector timeout recovered', status: 'warn' },
    { id: 'a3', when: 'Today 08:12', event: 'PII redacted in prompt', status: 'ok' },
    { id: 'a4', when: 'Yesterday 16:02', event: 'Knowledge lookup served', status: 'ok' },
    { id: 'a5', when: 'Yesterday 14:40', event: 'Topic fence flagged an off-scope ask', status: 'warn' },
    { id: 'a6', when: 'Yesterday 11:18', event: 'Grounded answer with two citations', status: 'ok' },
  ],
};

const MONITOR_BY_AGENT: Record<string, OmniChatMonitorInsight> = {
  'oc-benefits': {
    conversations: 412,
    tokens: 210440,
    latencyMs: 510,
    errorRate: 0.4,
    health: { score: 94, label: 'Healthy' },
    delta: { conversations: 12.6, tokens: 5.4, latencyMs: -6.1, errorRate: -0.1 },
    days: monitorDays([42, 48, 51, 39, 55, 61, 58], [540, 520, 500, 560, 490, 480, 510]),
    policies: { allowed: 392, flagged: 14, blocked: 6 },
    connectors: [connectorRow('knowledge', 268, 8, 1), connectorRow('sharepoint', 141, 11, 2)],
    groundedPct: 96,
    blocksToday: 1,
    activity: [
      { id: 'b1', when: 'Today 10:22', event: 'Leave policy answer served with citations', status: 'ok' },
      { id: 'b2', when: 'Today 09:41', event: 'PII redacted from an enrollment prompt', status: 'ok' },
      { id: 'b3', when: 'Today 09:05', event: 'SharePoint fetch completed', status: 'ok' },
      { id: 'b4', when: 'Today 08:18', event: 'Grounding refused an unsourced claim', status: 'ok' },
      { id: 'b5', when: 'Yesterday 17:18', event: 'Enrollment question escalated to HR', status: 'warn' },
      { id: 'b6', when: 'Yesterday 15:02', event: 'Knowledge lookup served HR Benefits', status: 'ok' },
      { id: 'b7', when: 'Yesterday 11:44', event: 'Topic fence held a payroll question', status: 'warn' },
    ],
  },
  'oc-sales': {
    conversations: 186,
    tokens: 156880,
    latencyMs: 720,
    errorRate: 1.1,
    health: { score: 78, label: 'Watch' },
    delta: { conversations: 4.2, tokens: 9.8, latencyMs: 11.4, errorRate: 0.3 },
    days: monitorDays([16, 21, 18, 24, 29, 22, 27], [690, 740, 710, 780, 760, 700, 720]),
    policies: { allowed: 164, flagged: 15, blocked: 7 },
    connectors: [
      connectorRow('sql_server', 72, 14, 4),
      connectorRow('teams', 58, 6, 1),
      connectorRow('sharepoint', 41, 5, 1),
    ],
    groundedPct: 84,
    blocksToday: 3,
    activity: [
      { id: 'c1', when: 'Today 11:03', event: 'Account brief generated from CRM notes', status: 'ok' },
      { id: 'c2', when: 'Today 09:26', event: 'Confirm-write paused a CRM update', status: 'warn' },
      { id: 'c3', when: 'Today 07:48', event: 'SQL Server query slow', status: 'warn' },
      { id: 'c4', when: 'Yesterday 16:55', event: 'Prohibited content blocked a pitch rewrite', status: 'error' },
      { id: 'c5', when: 'Yesterday 15:11', event: 'Teams thread ingested', status: 'ok' },
      { id: 'c6', when: 'Yesterday 13:04', event: 'SharePoint playbook cited in brief', status: 'ok' },
      { id: 'c7', when: 'Yesterday 10:22', event: 'Latency spike on research gateway', status: 'warn' },
    ],
  },
  'oc-helpdesk': {
    conversations: 903,
    tokens: 318220,
    latencyMs: 430,
    errorRate: 1.8,
    health: { score: 71, label: 'Watch' },
    delta: { conversations: 18.1, tokens: 7.2, latencyMs: -2.4, errorRate: 0.5 },
    days: monitorDays([88, 92, 79, 101, 97, 84, 90], [410, 440, 460, 400, 390, 450, 430]),
    policies: { allowed: 812, flagged: 61, blocked: 30 },
    connectors: [
      connectorRow('knowledge', 410, 22, 6),
      connectorRow('slack', 248, 18, 4),
      connectorRow('web', 132, 31, 12),
    ],
    groundedPct: 88,
    blocksToday: 8,
    activity: [
      { id: 'd1', when: 'Today 12:16', event: 'Password reset runbook sent', status: 'ok' },
      { id: 'd2', when: 'Today 11:08', event: 'Prompt injection attempt ignored', status: 'ok' },
      { id: 'd3', when: 'Today 10:51', event: 'Web lookup failed once', status: 'error' },
      { id: 'd4', when: 'Today 09:33', event: 'Confirm-write asked before Slack post', status: 'warn' },
      { id: 'd5', when: 'Today 08:09', event: 'Slack notification posted', status: 'ok' },
      { id: 'd6', when: 'Yesterday 18:22', event: 'Hardware ticket escalated', status: 'warn' },
      { id: 'd7', when: 'Yesterday 16:40', event: 'IT runbook cited for VPN issue', status: 'ok' },
      { id: 'd8', when: 'Yesterday 14:05', event: 'Web connector timed out then recovered', status: 'error' },
    ],
  },
};

function fallbackConnectors(ids: string[]): OmniChatMonitorInsight['connectors'] {
  return ids.map((id, index) =>
    connectorRow(id, 18 - index * 2, index === 0 ? 2 : 1, index > 1 ? 1 : 0)
  );
}

export function getAgentMonitor(id: string, connectorIds: string[] = []): OmniChatMonitorInsight {
  const base = MONITOR_BY_AGENT[id] ?? {
    ...DEFAULT_MONITOR,
    connectors: fallbackConnectors(connectorIds),
  };
  if (!connectorIds.length) return base;
  const byId = new Map(base.connectors.map((row) => [row.id, row]));
  return {
    ...base,
    connectors: connectorIds.map((connectorId) => byId.get(connectorId) ?? connectorRow(connectorId, 12, 1, 0)),
  };
}
